import { useEffect, useLayoutEffect, useRef, useState } from "react";
import DescopeWc from "@descope/web-component";

import type { RemoteCell } from "@/lib/cells/cells";
import { cellStoragePrefix, storeCellSession } from "@/lib/cells/cell-session";
import { useTheme } from "@/lib/theme/use-theme";

declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "descope-wc": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        "project-id": string;
        "flow-id": string;
        "storage-prefix": string;
        "redirect-url": string;
        "store-last-authenticated-user": string;
        tenant?: string;
      };
    }
  }
}

type SdkOverrides = typeof DescopeWc.sdkConfigOverrides;

let overridesBeforeCellFlow: SdkOverrides | undefined;
let cellFlowOverrides: SdkOverrides | undefined;

/**
 * react-sdk's <Descope> sets class-wide overrides on descope-wc: no token
 * persistence, and the primary session's request hook. A cell flow must not
 * inherit them, so they are swapped out before the element is created and put
 * back on unmount. Safe under StrictMode's double invocation.
 */
function applyCellFlowOverrides() {
  const current = DescopeWc.sdkConfigOverrides;
  if (!cellFlowOverrides || current !== cellFlowOverrides) {
    overridesBeforeCellFlow = current;
    cellFlowOverrides = {
      baseHeaders: current.baseHeaders,
      persistTokens: true,
    };
  }
  DescopeWc.sdkConfigOverrides = cellFlowOverrides;
}

function useCellFlowOverrides() {
  useState(applyCellFlowOverrides);
  useEffect(() => {
    applyCellFlowOverrides();
    return () => {
      if (overridesBeforeCellFlow) {
        DescopeWc.sdkConfigOverrides = overridesBeforeCellFlow;
      }
    };
  }, []);
}

interface CellSignInFlowProps {
  cell: RemoteCell;
  onSuccess: () => void;
}

/**
 * The cell's Descope sign-in flow. Its tokens are stored under the cell's
 * prefix, so the primary session and its last-user hint are left untouched.
 */
export function CellSignInFlow({ cell, onSuccess }: CellSignInFlowProps) {
  const { theme } = useTheme();
  const flowRef = useRef<HTMLElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [flowError, setFlowError] = useState<string | null>(null);

  useCellFlowOverrides();

  // descope-wc exposes `theme` as a read-only property, and React 19 assigns
  // props that exist on a custom element as properties, so set the attribute
  // before the element initialises.
  useLayoutEffect(() => {
    flowRef.current?.setAttribute("theme", theme === "dark" ? "dark" : "light");
  }, [theme]);

  useEffect(() => {
    const flow = flowRef.current;
    if (!flow) {
      return;
    }

    const onReady = () => {
      setIsReady(true);
    };
    const onError = (event: Event) => {
      const detail = (
        event as CustomEvent<{ errorDescription?: string } | null>
      ).detail;
      console.error(`Sign-in to the ${cell.id} cell failed:`, detail);
      setFlowError(detail?.errorDescription ?? "Sign-in failed. Try again.");
    };

    const handleSuccess = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          sessionJwt?: string;
          refreshJwt?: string;
        } | null>
      ).detail;
      storeCellSession(cell.id, detail?.sessionJwt, detail?.refreshJwt);
      onSuccess();
    };

    flow.addEventListener("ready", onReady);
    flow.addEventListener("success", handleSuccess);
    flow.addEventListener("error", onError);
    return () => {
      flow.removeEventListener("ready", onReady);
      flow.removeEventListener("success", handleSuccess);
      flow.removeEventListener("error", onError);
    };
  }, [cell.id, onSuccess]);

  return (
    <>
      {flowError && (
        <p role="alert" className="text-destructive text-center text-sm">
          {flowError}
        </p>
      )}
      {!isReady && (
        <p className="text-muted-foreground text-center text-sm">
          Loading sign-in…
        </p>
      )}
      <descope-wc
        ref={flowRef}
        project-id={cell.remote.descopeProjectId}
        flow-id={cell.remote.flowId}
        tenant={cell.remote.descopeTenantId}
        storage-prefix={cellStoragePrefix(cell.id)}
        redirect-url={window.location.href}
        store-last-authenticated-user="false"
      />
    </>
  );
}
